export type GraphicsQuality = 'low' | 'medium' | 'high';
export type FrameTarget = '30' | '60' | '120';

export interface Settings {
  masterVolume: number;
  effectsVolume: number;
  graphicsQuality: GraphicsQuality;
  frameTarget: FrameTarget;
  showFps: boolean;
}

const KEY = 'chroma.settings.v1';

export const DEFAULT_SETTINGS: Settings = {
  masterVolume: 0.7,
  effectsVolume: 0.9,
  graphicsQuality: 'medium',
  frameTarget: '60',
  showFps: true,
};

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      masterVolume: clamp(parsed.masterVolume ?? DEFAULT_SETTINGS.masterVolume, 0, 1),
      effectsVolume: clamp(parsed.effectsVolume ?? DEFAULT_SETTINGS.effectsVolume, 0, 1),
      graphicsQuality: isGraphicsQuality(parsed.graphicsQuality)
        ? parsed.graphicsQuality
        : DEFAULT_SETTINGS.graphicsQuality,
      frameTarget: isFrameTarget(parsed.frameTarget)
        ? parsed.frameTarget
        : DEFAULT_SETTINGS.frameTarget,
      showFps: typeof parsed.showFps === 'boolean' ? parsed.showFps : DEFAULT_SETTINGS.showFps,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

export function isGraphicsQuality(value: unknown): value is GraphicsQuality {
  return value === 'low' || value === 'medium' || value === 'high';
}

export function isFrameTarget(value: unknown): value is FrameTarget {
  return value === '30' || value === '60' || value === '120';
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // storage may be unavailable
  }
}

export function applyGraphicsQuality(quality: GraphicsQuality): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.graphics = quality;
}

export function applyFrameTarget(target: FrameTarget): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.frameTarget = target;
}
